// src/hooks/useAuthorization.tsx
import { useState } from 'react';
import AuthorizationModal from '../components/common/AuthorizationModal/AuthorizationModal';
import { useAuth } from '../context/AuthContext';

interface UseAuthorizationReturn {
  requestAuthorization: (
    action: string,
    actionName: string,
    actionData?: any
  ) => Promise<string | null>;
  AuthorizationComponent: React.FC;
}

export const useAuthorization = (): UseAuthorizationReturn => {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState('');
  const [actionName, setActionName] = useState('');
  const [actionData, setActionData] = useState<any>({});
  const [resolveAuth, setResolveAuth] = useState<((value: string | null) => void) | null>(null);
  const { user } = useAuth();

  /**
   * Solicitar autorización antes de ejecutar una acción
   * Retorna una Promise que se resuelve cuando se autoriza o cancela
   * Si el usuario es Administrador, se autoriza automáticamente
   */
  const requestAuthorization = (
    actionCode: string,
    actionDisplayName: string,
    data: any = {}
  ): Promise<string | null> => {
    // Administradores no necesitan autorización (retorna cadena vacía = bypass sin código)
    if (user?.Rol === 'Administrador') {
      return Promise.resolve('');
    }

    return new Promise((resolve) => {
      setAction(actionCode);
      setActionName(actionDisplayName);
      setActionData(data);
      setOpen(true);
      setResolveAuth(() => resolve);
    });
  };

  /**
   * Cerrar modal de autorización
   */
  const closeModal = () => {
    setOpen(false);
    if (resolveAuth) {
      resolveAuth(null);
      setResolveAuth(null);
    }
    // Reset después de un pequeño delay para evitar flickering
    setTimeout(() => {
      setAction('');
      setActionName('');
      setActionData({});
    }, 300);
  };

  /**
   * Callback cuando la autorización es exitosa — recibe el código usado
   */
  const handleAuthorized = (code: string) => {
    if (resolveAuth) {
      resolveAuth(code);
      setResolveAuth(null);
    }
    setOpen(false);
    setTimeout(() => {
      setAction('');
      setActionName('');
      setActionData({});
    }, 300);
  };

  /**
   * Componente de autorización listo para renderizar
   */
  const AuthorizationComponent: React.FC = () => (
    <AuthorizationModal
      open={open}
      onClose={closeModal}
      action={action}
      actionName={actionName}
      actionData={actionData}
      onAuthorized={handleAuthorized}
    />
  );

  return {
    requestAuthorization,
    AuthorizationComponent,
  };
};